/**
 * Shared authenticated fetch base for every web API client (matchClient,
 * economyClient, retentionClient, tournamentClient, privateClient — W2…W6).
 *
 * Auth branch (plan §W2 / risk #3):
 * - PROD: a Telegram WebApp runtime exposes raw `initData`, so we send
 *   `Authorization: tma <initDataRaw>`. The api middleware validates the HMAC.
 * - DEV: a plain browser (`vite dev`) has no Telegram runtime, so `initDataRaw`
 *   is null. When `import.meta.env.DEV` and `VITE_DEV_USER` is set we send
 *   `X-Dev-User: <id>:<name>`, which the api's env-gated bypass accepts. The
 *   bypass binding is absent in deployed Workers, so this header is dead in prod.
 *
 * The dev branch is structurally unreachable in prod: it requires both
 * `import.meta.env.DEV` (false in a production build) and a missing initData.
 */
import type { z } from 'zod'
import { retrieveRawInitData } from '@telegram-apps/sdk'

/** Base URL of the api Worker. Defaults to the local wrangler dev port. */
function apiBase(): string {
  return (import.meta.env.VITE_API_BASE as string | undefined) ?? 'http://localhost:8787'
}

/** `<id>:<name>` dev identity for the browser bypass, or undefined in Telegram. */
function devUser(): string | undefined {
  return import.meta.env.VITE_DEV_USER as string | undefined
}

/**
 * Raw Telegram initData string, or null outside a Telegram runtime.
 *
 * `window.Telegram.WebApp.initData` is the primary source (always present inside
 * the Mini App). `retrieveRawInitData()` from the SDK is a fallback for runtimes
 * that populate launch params without the legacy global. Returns null in a plain
 * dev browser, which routes the request onto the `X-Dev-User` branch.
 */
export function getInitDataRaw(): string | null {
  try {
    const fromGlobal = window.Telegram?.WebApp?.initData
    if (fromGlobal) return fromGlobal
  } catch {
    // window.Telegram absent — fall through to the SDK.
  }
  try {
    const fromSdk = retrieveRawInitData()
    if (fromSdk) return fromSdk
  } catch {
    // Not a Telegram environment.
  }
  return null
}

/** Typed error carrying the HTTP status so callers can branch (401/402/409/…). */
export class ApiError extends Error {
  readonly status: number
  constructor(status: number, message: string) {
    super(message)
    this.name = 'ApiError'
    this.status = status
  }
}

/** Thrown when the request never reached the server (offline, DNS, CORS). */
export class NetworkError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'NetworkError'
  }
}

/**
 * Build the auth header for a request. Telegram initData wins (prod); otherwise
 * the dev bypass header is used only under `import.meta.env.DEV`.
 */
function authHeaders(): Record<string, string> {
  const initDataRaw = getInitDataRaw()
  if (initDataRaw) return { Authorization: `tma ${initDataRaw}` }
  const dev = devUser()
  if (import.meta.env.DEV && dev) return { 'X-Dev-User': dev }
  return {}
}

export type AuthedFetchInit = {
  readonly method?: string
  /** JSON body — serialized automatically with a `Content-Type` header. */
  readonly body?: unknown
  readonly headers?: Record<string, string>
}

/**
 * Authenticated fetch against the api Worker. Injects the auth header, JSON-
 * serializes the body, and normalizes failures into typed errors. Returns the
 * parsed JSON response (use `parseJson` with a zod schema to validate it).
 */
export async function authedFetch(path: string, init: AuthedFetchInit = {}): Promise<unknown> {
  const headers: Record<string, string> = { ...authHeaders(), ...init.headers }
  let body: string | undefined
  if (init.body !== undefined) {
    headers['Content-Type'] = 'application/json'
    body = JSON.stringify(init.body)
  }

  let resp: Response
  try {
    resp = await fetch(`${apiBase()}${path}`, { method: init.method ?? 'GET', headers, body })
  } catch (err) {
    console.error('[apiClient] network error', path, err)
    throw new NetworkError('네트워크 오류로 요청에 실패했습니다')
  }

  if (!resp.ok) {
    console.error('[apiClient] request failed', path, resp.status)
    throw new ApiError(resp.status, `요청에 실패했습니다 (${resp.status})`)
  }

  try {
    return await resp.json()
  } catch (err) {
    console.error('[apiClient] malformed JSON', path, err)
    throw new ApiError(resp.status, '응답 형식 오류')
  }
}

/**
 * Validate an unknown response against a zod schema, throwing a typed `ApiError`
 * on mismatch so client functions get a parsed, typed value or a clear failure.
 */
export function parseJson<T>(schema: z.ZodType<T>, data: unknown): T {
  const result = schema.safeParse(data)
  if (!result.success) {
    console.error('[apiClient] response validation failed', result.error.issues)
    throw new ApiError(0, '응답 검증에 실패했습니다')
  }
  return result.data
}
